import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Warning, Rocket, DownloadSimple, ShieldCheck, Trash } from "@phosphor-icons/react";

const GROUPS = [
  { key: "customers", label: "Customers + their Documents", danger: true },
  { key: "applications", label: "All Applications (bills, payments, status history)", danger: true },
  { key: "complaints", label: "All Complaints", danger: false },
  { key: "rent_agreements", label: "All Rent Agreements + deposit receipts", danger: false },
  { key: "pos_bills", label: "All POS Bills", danger: false },
  { key: "ai_drafts", label: "AI Draft History", danger: false },
  { key: "audit_logs", label: "Audit Logs (old activity trail)", danger: false },
  { key: "counters", label: "Number Series (Customer/App/POS/Invoice will restart from 00001)", danger: true },
];

const KEEP = [
  "User Accounts + Roles", "Services Catalog (114+ CSC / Aaple Sarkar / MahaOnline)",
  "POS Products (Stationery / Printing master)", "System Settings", "AI Templates",
];

export default function GoLive() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [counts, setCounts] = useState({});
  const [selected, setSelected] = useState([]); // start with NONE selected — user must consciously opt-in per group
  const [confirmText, setConfirmText] = useState("");
  const [openConfirm, setOpenConfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/system/counts");
      setCounts(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Only Super Admin can access this page"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (user && user.role !== "super_admin") {
    return (
      <div className="p-8">
        <Card className="p-6 border-red-300 bg-red-50 text-red-800 max-w-xl">
          <div className="text-lg font-semibold" style={{ fontFamily: "var(--font-heading)" }}>Restricted</div>
          <p className="text-sm mt-2">Only <b>Super Admin</b> can access the Go-Live wizard. Your current role: <b>{user.role}</b>.</p>
        </Card>
      </div>
    );
  }

  const toggle = (k) => setSelected((s) => s.includes(k) ? s.filter((x) => x !== k) : [...s, k]);

  const downloadBackup = async () => {
    try {
      setBusy(true);
      const { data } = await api.get("/system/backup");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `priyanka-erp-backup-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Backup downloaded — keep this file safe");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Backup failed"); }
    finally { setBusy(false); }
  };

  const launch = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/system/go-live", { groups: selected, confirmation: confirmText });
      const total = Object.values(data.wiped || {}).reduce((s, n) => s + n, 0);
      toast.success(`Live! ${total} records cleared across ${Object.keys(data.wiped || {}).length} collections`);
      setOpenConfirm(false);
      setTimeout(() => nav("/"), 800);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Reset failed"); }
    finally { setBusy(false); }
  };

  const readyToLaunch = confirmText === "RESET" && selected.length > 0;

  return (
    <div className="p-8 max-w-4xl">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] text-orange-600 font-semibold mb-2 flex items-center gap-2"><Rocket size={14} weight="duotone" /> Launch Portal · System Recovery</div>
        <h1 className="text-4xl font-bold text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>🚀 Launch Portal &amp; ⚠️ System Recovery</h1>
        <p className="text-sm text-slate-600 mt-2">Publish the platform for live customers, or perform a controlled system recovery (wipe testing data, reset number series). Recovery actions are <b>irreversible</b>.</p>
      </div>

      <div className="rounded-md border border-red-300 bg-red-50 p-4 mb-6 flex items-start gap-2 text-sm text-red-900" data-testid="go-live-warning">
        <Warning size={18} weight="duotone" className="mt-0.5 shrink-0" />
        <div>
          <b>You are about to wipe live data.</b> The records below cannot be recovered without a backup. Download the JSON backup first, then proceed.
        </div>
      </div>

      <Card className="p-5 bg-white border-slate-200 mb-4">
        <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500 mb-3">Step 1 · Download Backup</div>
        <Button data-testid="go-live-download" onClick={downloadBackup} disabled={busy} className="bg-slate-900 hover:bg-slate-800 text-white gap-2">
          <DownloadSimple size={16} /> Download Full JSON Backup
        </Button>
        <div className="text-xs text-slate-500 mt-2">Includes every collection: customers, applications, bills, complaints, POS, rent agreements, users, services, and audit logs.</div>
      </Card>

      <Card className="p-5 bg-white border-slate-200 mb-4">
        <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500 mb-3">Step 2 · Choose what to wipe</div>
        <div className="space-y-2">
          {GROUPS.map((g) => {
            const collections = ({
              customers: ["customers", "customer_documents"],
              applications: ["applications"],
              complaints: ["complaints"],
              rent_agreements: ["rent_agreements"],
              pos_bills: ["pos_bills"],
              ai_drafts: ["ai_drafts"],
              audit_logs: ["audit_logs"],
              counters: ["counters"],
            })[g.key] || [];
            const total = collections.reduce((s, c) => s + (counts[c] || 0), 0);
            return (
              <label key={g.key} className={`flex items-center gap-3 p-3 rounded-md border cursor-pointer transition-colors ${selected.includes(g.key) ? (g.danger ? "border-red-300 bg-red-50/40" : "border-orange-300 bg-orange-50/40") : "border-slate-200 hover:bg-slate-50"}`}>
                <Checkbox checked={selected.includes(g.key)} onCheckedChange={() => toggle(g.key)} data-testid={`go-live-group-${g.key}`} />
                <div className="flex-1">
                  <div className="text-sm font-medium text-slate-900">{g.label}</div>
                  <div className="text-[10px] text-slate-500">Currently: {total} records</div>
                </div>
                {g.danger && <span className="text-[10px] text-red-600 font-semibold uppercase tracking-widest">Irreversible</span>}
              </label>
            );
          })}
        </div>
      </Card>

      <Card className="p-5 bg-emerald-50 border-emerald-200 mb-6">
        <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-emerald-700 mb-2 flex items-center gap-2"><ShieldCheck size={12} /> Preserved (never wiped)</div>
        <ul className="text-sm text-slate-800 grid grid-cols-1 md:grid-cols-2 gap-1">
          {KEEP.map((k) => <li key={k}>✓ {k}</li>)}
        </ul>
      </Card>

      <Card className="p-5 bg-white border-slate-200 mb-6">
        <Label className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Step 3 · Type <span className="text-red-600 font-bold">RESET</span> to confirm</Label>
        <Input
          data-testid="go-live-confirm-input"
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          placeholder="RESET"
          className="mt-2 h-11 text-lg font-mono uppercase tracking-widest"
        />
      </Card>

      <div className="flex gap-2 justify-end">
        <Button variant="outline" onClick={() => nav("/")}>Cancel</Button>
        <Button
          data-testid="go-live-launch"
          disabled={!readyToLaunch || busy}
          onClick={() => setOpenConfirm(true)}
          className="bg-red-600 hover:bg-red-700 text-white gap-2 h-11 px-6"
        >
          <Rocket size={16} weight="fill" /> Launch Production
        </Button>
      </div>

      <AlertDialog open={openConfirm} onOpenChange={setOpenConfirm}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle style={{ fontFamily: "var(--font-heading)" }} className="flex items-center gap-2"><Trash size={18} className="text-red-600" /> Final confirmation</AlertDialogTitle>
            <AlertDialogDescription>
              You are about to permanently delete data from <b>{selected.length}</b> group(s). Users, roles, services, POS products and system settings will be preserved. This cannot be undone. Are you sure?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="go-live-final-confirm" disabled={busy} onClick={launch} className="bg-red-600 hover:bg-red-700 text-white">
              {busy ? "Wiping…" : "Yes, wipe & go live"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
